import { useState } from 'react';
import type { AuditScoreResult, PeerSearchResult, SampleConfidence } from '@/types/gmb';
import { formatCoverageLevelLabel } from '@/pages/gmb-audit/utils/reportDisplay';

// UI-2C｜Coverage Compact。
// 將原本偏大的 Coverage 區塊縮成 compact summary：資料覆蓋度 / 可評分滿分 / Peer sample / 可信度。
// 只讀 Backend 真實值：dataCoveragePercent / availableMaxScore / peerComparison；不重算任何分數。

const CONFIDENCE_LABEL: Record<SampleConfidence, string> = {
  HIGH: '高',
  MEDIUM: '中',
  LOW: '低',
  NONE: '不足',
};

function StatCard({ icon, label, value, hint }: { icon: string; label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-background-300 bg-background-50 p-4 shadow-card md:p-5">
      <p className="flex items-center gap-1.5 text-sm font-medium text-foreground-500">
        <i className={`${icon} text-base text-primary-600`} />
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold leading-none text-foreground-950 md:text-3xl">{value}</p>
      {hint && <p className="mt-1.5 text-xs text-foreground-400 md:text-sm">{hint}</p>}
    </div>
  );
}

export default function ReportCoverageSection({
  result,
  peerComparison,
}: {
  result: AuditScoreResult;
  peerComparison?: PeerSearchResult;
}) {
  const [notesOpen, setNotesOpen] = useState(false);
  const notes = result.dataNotes || [];

  const sampleCount = peerComparison ? peerComparison.sampleCount : null;
  const confidence = peerComparison ? CONFIDENCE_LABEL[peerComparison.sampleConfidence] ?? '不足' : null;

  return (
    <section className="ds-container pt-12 md:pt-16">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <i className="ri-database-2-line text-xl" />
        </span>
        <h2 className="text-xl md:text-2xl">評分資料覆蓋與限制</h2>
      </div>
      <p className="mb-5 text-base text-foreground-500">本健診僅依目前可取得的公開資料計算可評分項目。</p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <StatCard
          icon="ri-pie-chart-2-line"
          label="資料覆蓋度"
          value={`${result.dataCoveragePercent}%`}
          hint={formatCoverageLevelLabel(result.coverageLevel)}
        />
        <StatCard
          icon="ri-scales-3-line"
          label="可評分滿分"
          value={`${result.availableMaxScore} / 100`}
        />
        <StatCard
          icon="ri-building-2-line"
          label="同業樣本"
          value={sampleCount === null ? '—' : `${sampleCount} 家`}
        />
        <StatCard
          icon="ri-shield-check-line"
          label="可信度"
          value={confidence ?? '—'}
        />
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl border border-background-300 bg-background-100 p-4 text-foreground-600">
        <i className="ri-information-line mt-0.5 text-lg text-primary-700" />
        <p className="text-sm leading-relaxed md:text-base">
          覆蓋率代表本次有多少評分權重具備足夠資料可判定，不代表商家內容已全部完善。即使覆蓋率為 100%，仍可能同時存在照片、評論或網站等項目得分偏低。
        </p>
      </div>

      {notes.length > 0 && (
        <div className="mt-4 rounded-2xl border border-background-300 bg-background-50 shadow-card">
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            aria-expanded={notesOpen}
            className="flex w-full cursor-pointer items-center justify-between gap-3 px-5 py-4 text-left"
          >
            <span className="flex items-center gap-2 text-base font-bold text-foreground-800">
              <i className="ri-information-line text-lg text-primary-700" />
              資料說明（{notes.length}）
            </span>
            <i
              className={`${notesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-foreground-400`}
            />
          </button>
          {notesOpen && (
            <ul className="space-y-2.5 border-t border-background-200 px-5 pb-5 pt-4">
              {notes.map((note, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-600 md:text-base"
                >
                  <i className="ri-subtract-line mt-1 text-foreground-400" />
                  {note}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="mt-4 text-sm leading-relaxed text-foreground-400">
        資料來源為 Google Places 公開資料，部分欄位可能因商家設定或 Google 資料狀態而無法取得。
      </p>
    </section>
  );
}